import { useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TRAILER_PHOTO_TYPE_LABEL,
  type TrailerEventPhoto,
} from "@/lib/trailer-event-photos";

interface Props {
  photos: TrailerEventPhoto[];
}

/**
 * Slide-out gallery for a trailer's captured yard photos (Trailer, Seal,
 * License Plate). Renders one photo at a time with left/right chevrons
 * and a type badge overlay so the operator can identify each capture.
 */
export function TrailerPhotoGallery({ photos }: Props) {
  const [index, setIndex] = useState(0);

  if (!photos.length) return null;

  const safeIndex = Math.min(index, photos.length - 1);
  const current = photos[safeIndex];
  const hasMultiple = photos.length > 1;

  const prev = () => setIndex((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIndex((i) => (i + 1) % photos.length);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="relative bg-muted">
          {current.signedUrl ? (
            <img
              src={current.signedUrl}
              alt={TRAILER_PHOTO_TYPE_LABEL[current.photo_type]}
              className="w-full h-48 object-cover"
            />
          ) : (
            <div className="w-full h-48 flex items-center justify-center text-muted-foreground">
              <ImageOff className="h-6 w-6" />
            </div>
          )}

          <div className="absolute top-2 left-2">
            <Badge variant="secondary" className="text-xs shadow-sm">
              {TRAILER_PHOTO_TYPE_LABEL[current.photo_type]}
            </Badge>
          </div>

          {hasMultiple && (
            <>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={prev}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full shadow-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={next}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full shadow-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="absolute bottom-2 right-2">
                <Badge variant="secondary" className="text-xs shadow-sm tabular-nums">
                  {safeIndex + 1} / {photos.length}
                </Badge>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}